import { Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import NavBreadcrumb from 'dashboard/components-lib/NavBreadcrumb';
import { FullScreenHeaderContainer, StickyContainer } from 'dashboard/layouts/ShowViewLayoutV2';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionStatus,
} from 'dashboard/models/inquiry-template-version';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import { Variant } from 'design-system/theme';
import { assertNever } from 'lib/utils/types';

const StyledStickyContainer = styled(StickyContainer)`
  // Lower the z-index so that a tooltip can render over it
  z-index: 5;
`;

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
  padding-left: -${Spacing[2]};
  margin-right: ${Spacing[4]};
  overflow: hidden;
`;

const StatusWrapper = styled.div`
  display: flex;
  gap: ${Spacing[2]};
  margin-top: auto;
  margin-bottom: auto;
`;

const HeaderButton = styled(Button).attrs(() => ({ width: 'auto' }))``;

const StatusSticker = ({ status }: { status: InquiryTemplateVersionStatus }) => {
  const getStickerStatus = (): [Exclude<Variant, 'inverse'>, string] => {
    switch (status) {
      case InquiryTemplateVersionStatus.Published: {
        return ['success', 'Published'];
      }
      case InquiryTemplateVersionStatus.Draft: {
        return ['primary', 'Draft'];
      }
      default:
        assertNever(status);
    }
  };
  const [variantType, statusMessage] = getStickerStatus();
  return <Sticker variant={variantType}>{statusMessage}</Sticker>;
};

export interface InquiryPreviewHeaderProps {
  inquiryTemplate: InquiryTemplate;
  inquiryTemplateVersion: InquiryTemplateVersion;
  inquiry: Inquiry | undefined;
  accountTypeName?: string | null;
  onBackClick: () => void;
  onPrefill?: () => void;
  onViewInquiry?: () => void;
  onRestart?: (options: { resetStubsAndPresets: boolean; resetPrefills: boolean }) => void;
}

export const InquiryPreviewHeader = ({
  inquiryTemplate,
  inquiryTemplateVersion,
  inquiry,
  accountTypeName,
  onBackClick,
  onPrefill,
  onViewInquiry,
  onRestart,
}: InquiryPreviewHeaderProps) => {
  const {
    attributes: { status: versionStatus },
  } = inquiryTemplateVersion;
  const breadcrumbs = [
    {
      displayName: 'Preview' + (inquiry ? ` (${inquiry.id})` : ''),
    },
  ];

  return (
    <>
      <StyledStickyContainer data-test='inquiry-template-header'>
        <FullScreenHeaderContainer>
          <HorizontalContainer>
            <NavBreadcrumb
              name={inquiryTemplate.attributes.name}
              breadcrumbItems={breadcrumbs}
              onBackClick={onBackClick}
            />
            <StatusWrapper>
              <StatusSticker status={versionStatus} />
              {accountTypeName && (
                <Sticker variant='basic'>{`Account Type: ${accountTypeName}`}</Sticker>
              )}
            </StatusWrapper>
          </HorizontalContainer>
          <HStack spacing={Spacing[3]}>
            {onViewInquiry && (
              <HeaderButton variant='basic' onClick={onViewInquiry}>
                View Inquiry
              </HeaderButton>
            )}
            {onPrefill && (
              <HeaderButton variant='basic' onClick={onPrefill}>
                Prefill
              </HeaderButton>
            )}
            {onRestart && (
              <DropdownMenu trigger={<HeaderButton variant='basic'>Restart</HeaderButton>}>
                <DropdownMenu.Item
                  id='inquiry-preview-header__restart-keep-settings'
                  onClick={() => onRestart({ resetStubsAndPresets: false, resetPrefills: false })}
                >
                  Keep settings
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  id='inquiry-preview-header__restart-reset-settings'
                  onClick={() => onRestart({ resetStubsAndPresets: true, resetPrefills: true })}
                >
                  Reset settings
                </DropdownMenu.Item>
              </DropdownMenu>
            )}
          </HStack>
        </FullScreenHeaderContainer>
      </StyledStickyContainer>
    </>
  );
};
